import type { CountryCode } from '../payment-gateways/core/types';
import { env } from '../config/env';
import {
  COUNTRY_OVERRIDE_PARAM,
  HOSTNAME_SUFFIXES,
  LEGACY_COUNTRY_ALIASES,
} from '../constants/countries';

/**
 * Resolves which country this page is serving. The data it reads — the
 * hostname map and the accepted country strings — lives in
 * src/constants/countries.ts; this file is only the resolution order.
 *
 * That order is: hostname, then the QA query override, then the env var.
 * Hostname comes first because each country is a separate deployment on
 * its own domain. The env var is a local-dev-only fallback and must never
 * be what production relies on — that's exactly the "env says X, payload
 * says Y" ambiguity that made the old resolver fragile.
 */

/**
 * Normalizes whatever country string the booking app sends (`IND`, `IN`,
 * `TH`, ...) into the one format the rest of this app uses. Returns null for
 * anything unrecognized rather than guessing.
 */
export function normalizeCountry(
  raw: string | undefined | null
): CountryCode | null {
  if (!raw) return null;
  const normalized = LEGACY_COUNTRY_ALIASES[raw.trim().toUpperCase()];

  return normalized ?? null;
}

function resolveFromHostname(hostname: string): CountryCode | null {
  const match = HOSTNAME_SUFFIXES.find(([suffix]) => hostname.endsWith(suffix));

  return match?.[1] ?? null;
}

function resolveFromQueryOverride(search: URLSearchParams): CountryCode | null {
  return normalizeCountry(search.get(COUNTRY_OVERRIDE_PARAM));
}

/**
 * Resolves the active country once, in priority order:
 * hostname → explicit QA query override → env fallback (local dev only).
 * Returns null if nothing resolves — callers must treat that as an
 * unrecoverable config error, not silently render a blank page (§1).
 */
export function resolveCountry(
  hostname: string,
  search: URLSearchParams
): CountryCode | null {
  return (
    resolveFromHostname(hostname) ??
    resolveFromQueryOverride(search) ??
    normalizeCountry(env.VITE_COUNTRY)
  );
}
